<template>
  <div
    :class="['wrapper scrollable', {
      'can-scroll': scrollable && canScroll,
      'scrolled': scrolled || isOverlay,
      'scrolled--top-shadow': !showSearch
    }]"
    ref="wrapper"
    :style="!app.deviceIsDesktop && {height: wrapperHeight}"
  >
    <div :class="['container', {dark: showAnswers && (scrolled || isOverlay)}]"
          @scroll="scroll"
          ref="container"
    >

      <div id="spacer"
        v-show="!searchingStuck"
        ref="spacer"
        :style="{minHeight: spacerHeight}"
        @click="close"
        @touchstart="blurInput"
        @mousedown="blurInput"
      />

      <transition name="bottom" appear>
        <div class="selector-container"
          v-if="showAnswers"

        >
          <div v-show="!searchingStuck" class="arrow" ref="arrow" :class="{hidden: !scrollable, 'mid-scroll': midScroll}"/>
          <div
            :class="['input-container', {'scrollable': scrollable, 'stuck': stuckInput}]"
            ref="search"
            v-if="showSearch"
          >
            <div class="icon"/>
            <input
              :placeholder="placeholder"
              @focus="onFocus"
              @click="onFocus"
              type="text"
              ref="input"
              class="mousetrap"
              @input="onInput"
              @keydown="onKeydown"
            >
            <button class="clear" v-show="searching" @click="clearInput"/>
          </div>
          <div ref="list" class="list-container" :style="extraPadding ? {paddingBottom: extraPadding} : {}" @touchstart="blurInput" @mousedown="blurInput">
            <transition name="fade">
              <div class="list-overlay" v-if="!app.deviceIsDesktop && inputIsFocused && !searching" @keydown="inputIsFocused = false" @touchstart="inputIsFocused = false"/>
            </transition>
            <ul :class="{saving: saving, grouped: stuckInput && !searching}" v-for="list in arrangedOptions" :key="list.letter">
              <h5 v-show="stuckInput && !searching && list.letter !== '0'" class="seperator">{{ list.letter }}</h5>
              <li
                v-for="(item, index) in list.data"
                :key="`item-${item.id}`"
                :class="{selected: selectedAnswerId === item.id, prepend: list.letter === '0', 'hide-borders': item.hideBorders}"
                @click="submitAnswer(item.id)"
              >
                <span v-if="item.prepend" v-html="item.prepend"></span>
                <span>{{ item.label || item.value }}</span>
                <span v-if="item.subtitle" :class="['subtitle', {'subtitle--strong': subtitleStrong}]">{{ item.subtitle }}</span>
                <img v-else-if="item.image" :src="item.image" alt="">
              </li>
            </ul>
          </div>
        </div>
      </transition>
      <!-- used to hide the dark background showing on iOS on elastic scroll since all pseudo elements are in use -->
      <div class="white-underlay" v-if="!saving"></div>
    </div>
  </div>
</template>

<script>
  import Mousetrap from 'mousetrap'
  import { mapState } from 'vuex'

  export default {
    data () {
      return {
        selectedAnswerId: -1,
        saving: false,
        showAnswers: false,
        scrollable: true,
        scrolled: false,
        canScroll: true,
        midScroll: false,
        stuckInput: false,
        inputIsFocused: false,
        spacerHeight: null,
        wrapperHeight: null,
        keyword: '',
        smoothScrollPromise: Promise.resolve(),
        extraPadding: null,
        milestones: {
          geoLocation: 5,
          search: 10,
        },
      }
    },
    computed: {
      ...mapState(['app']),

      parsedOptions () {
        let newOptions

        newOptions = this.options.filter((item) => !item.subheader)

        // filter
        newOptions = newOptions.filter((item) =>
          (item.label || item.value)
          .toLowerCase()
          .indexOf(this.keyword
          .toLowerCase()) > -1)

        // sort
        newOptions = newOptions.sort((a, b) => {
          const stringA = a.label || a.value
          const stringB = b.label || b.value

          if (stringA < stringB) { return -1 }
          if (stringA > stringB) { return 1 }
          return 0
        })

        // identify
        newOptions = newOptions
          .map((option, index) => ({...option, id: index}))

        if (this.hasOther) {
          newOptions.push({
            id: newOptions.length,
            label: this.i18n.t('base:other'),
            isOther: true,
          })
        }
        return newOptions
      },

      arrangedOptions () {
        let options = Object.values(
          this.parsedOptions.reduce((acc, item) => {
            const string = item.label || item.value
            const firstLetter = item.prepend ? '0' : string[0].toLocaleUpperCase()
            if (item.isOther) {
              acc['-'] = { letter: '', data: [item] }
            } else if (!acc[firstLetter]) {
              acc[firstLetter] = { letter: firstLetter, data: [item] }
            } else {
              acc[firstLetter].data.push(item)
            }
            return acc
          }, {}))

        return options
      },

      searching () {
        return !!this.keyword
      },

      searchingStuck () {
        return this.searching && this.stuckInput && !this.smoothScrollPromise.pending
      },

      showSearch () {
        return this.options.length > this.milestones.search
      },

      showGeoLocation () {
        return this.options.length > this.milestones.geoLocation
      },

      arrowHeight () {
        return this.searching ? 0 : 16
      },
    },
    props: {
      options: {
        type: Array,
        required: true,
      },
      isOverlay: {
        type: Boolean,
        default: false,
      },
      hasOther: {
        type: Boolean,
        default: false,
      },
      placeholder: {
        type: String,
        default: 'Search...'
      },
      subtitleStrong: {
        type: Boolean,
        default: false
      },
    },

    mounted () {
      setTimeout(() => {
        this.wrapperHeight = `${window.innerHeight}px`
        this.setSpacerHeight()

        this.showAnswers = true
      }, 300)

      Mousetrap.bind(['down'], () => {
        if (this.selectedAnswerId === -1) {
          this.selectedAnswerId = 0
          return
        } else if (this.selectedAnswerId >= this.parsedOptions.length - 1) {
          return
        }

        this.selectedAnswerId++
        this.scrollToItem()
      })

      Mousetrap.bind(['up'], () => {
        if (this.selectedAnswerId <= 0) {
          this.selectedAnswerId = 0
          return
        }
        this.selectedAnswerId--
        this.scrollToItem()
      })

      Mousetrap.bind('enter', () => {
        if (this.selectedAnswerId === -1) return
        this.submitAnswer(this.selectedAnswerId)
      })
    },
    destroyed () {
      Mousetrap.unbind(['up', 'down', 'enter'])
    },

    methods: {
      submitAnswer (id) {
        this.selectedAnswerId = id
        const answer = this.parsedOptions[this.selectedAnswerId]
        this.saving = true

        setTimeout(() => {
          this.showAnswers = false
          this.scrolled = false
          setTimeout(() => {
            if (answer.isOther) return this.$emit('other')
            this.$emit('submit', answer)
          }, 700)
        }, 400)
      },

      setSpacerHeight () {
        let distance
        const target = document.querySelector('.actionhint') ||
          document.querySelector('.question') ||
          document.querySelector('.helper')

        let selectorHeight = 60 * this.parsedOptions.length + this.arrowHeight

        if (this.showSearch) { selectorHeight += 61 }

        if (this.isOverlay) {
          if ((selectorHeight * 100) / this.$refs.wrapper.getBoundingClientRect().height > 70) {
            distance = this.$refs.wrapper.getBoundingClientRect().height * 0.3
            this.spacerHeight = `${distance}px`
          } else {
            distance = this.$refs.wrapper.getBoundingClientRect().height - selectorHeight
            this.scrollable = false
            this.spacerHeight = `${distance}px`
          }
        } else {
          if (target) {
            distance =
            target.getBoundingClientRect().top +
            target.getBoundingClientRect().height +
            50 -
            this.$refs.wrapper.getBoundingClientRect().top
          } else {
            distance = this.$refs.wrapper.getBoundingClientRect().height * 0.5
          }

          let offset =
          this.$refs.wrapper.getBoundingClientRect().height - (selectorHeight + distance)

          if (offset > 0) {
            distance += offset
            this.scrollable = false
          }

          this.spacerHeight = `${distance}px`
        }

        if (this.showSearch) {
          const overflow = (distance + selectorHeight) - this.$refs.wrapper.getBoundingClientRect().height
          if (overflow < distance) this.extraPadding = `${(distance - overflow) + this.arrowHeight}px`
        }
      },

      scroll (e) {
        const scrollMax = e.target.scrollHeight - e.target.offsetHeight
        const scrollTop = Math.round(e.target.scrollTop)

        this.stuckInput =
          scrollTop - this.arrowHeight >= Math.floor(this.$refs.spacer.getBoundingClientRect().height)

        // bottom shadow
        this.canScroll = scrollTop < scrollMax
        //

        if (this.searching) return

        if (scrollTop >= 30) {
          this.midScroll = true
          if (!this.isOverlay) this.scrolled = true
        } else {
          this.midScroll = false
          if (!this.isOverlay) this.scrolled = false
        }
      },

      scrollToItem () {
        this.$nextTick(() => {
          this.$refs.list.querySelectorAll('li')[this.selectedAnswerId].scrollIntoView({block: 'center'})
        })
      },

      close () {
        if (!this.isOverlay) return
        this.showAnswers = false
        this.scrolled = false
        setTimeout(() => {
          this.$emit('close')
        }, 500)
      },

      onFocus () {
        if (!this.stuckInput) {
          this.stickInput()
        }
        this.inputIsFocused = true
      },

      onKeydown (e) {
        this.stickInput(!this.stuckInput)
        this.preventKeys(e)
      },

      onInput (e) {
        this.smoothScrollPromise.then(() => {
          this.syncValue(e.target.value)
          this.selectedAnswerId = -1
          this.$nextTick(() => this.stickInput(!this.stuckInput))
        })
      },

      blurInput () {
        const input = this.$refs.input
        if (input) input.blur()
      },

      stickInput (smooth = true) {
        if (this.smoothScrollPromise.pending) return
        this.$refs.container.scrollTo({
          top: this.$refs.spacer.getBoundingClientRect().height + this.arrowHeight,
          ...(smooth ? {behavior: 'smooth'} : {}),
        })

        smooth && this.observeSmoothScroll() // preventing consecutive smooth scrolls
      },

      clearInput () {
        this.syncValue('')
        this.$refs.input.focus()
        this.$nextTick(() => {
          this.$refs.container.scrollTop = this.$refs.spacer.getBoundingClientRect().height + this.arrowHeight
        })
      },

      observeSmoothScroll () {
        this.smoothScrollPromise = new Promise(resolve => {
          setInterval(() => {
            if (this.stuckInput) {
              this.smoothScrollPromise.pending = false
              if (this.$postmate && !this.app.deviceIsDesktop) {
                this.$postmate.emit('scrollToTop')
              }
              resolve()
            }
          }, 25)
          this.smoothScrollPromise.pending = true
        })
      },

      syncValue (value) {
        if (value === undefined) return
        const input = this.$refs.input.value

        if (this.keyword !== value || input !== value) {
          this.keyword = value
          this.$refs.input.value = value
        }
      },

      preventKeys (e) {
        // up & down keys
        if (e.which === 38 || e.which === 40) {
          e.preventDefault()
        }
      },
    },
  }
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @import "../assets/_settings";
  @import "../assets/mixins";

  #spacer {
    width: 100%;
    background: transparent;
    transition: background .4s;
    position: relative;
  }

  .selector-container {
    position: relative;
    width: 100%;
    background: #FFFFFF;
    border-radius: 15px 15px 0 0;
    box-shadow: 0px -11px 20px 0px rgba(0, 0, 0, 0.07);
    flex: 1;

    .arrow {
      position: relative;
      height: 16px;

      background-color: #ffffff;
      border-radius: 15px 15px 0 0;

      &:before {
        content: '';
        position: absolute;
        top: 8px;
        left: 14px;
        right: 0;
        margin: 0 auto;
        height: 4px;
        width: 16px;
        border-radius: 2px;
        background-color: #D6D6D6;
        transition: all .5s;
      }

      &:after {
        content: '';
        position: absolute;
        top: 8px;
        left: 0;
        right: 14px;
        margin: 0 auto;
        height: 4px;
        width: 16px;
        border-radius: 2px;
        background-color: #D6D6D6;
        transition: all .5s;
      }

      &.mid-scroll {
        &:before {
          width: 15px;
          right: 5px;
          transform: rotate(-35deg);
        }

        &:after {
          left: 5px;
          width: 15px;
          transform: rotate(35deg);
        }
      }

      &.hidden {
        &:before,
        &:after {
          display: none;
        }
      }
    }
  }

  .input-container {
    padding-left: 30px;
    position: relative;
    background: #FFFFFF;
    width: 100%;
    padding: 0 18px 0 30px;
    z-index: 999;
    position: sticky;
    top: -1px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    @include desktop-big {
      padding-left: 115px;
    }

    &.stuck {
      border-radius: 0;
      border-bottom: 1px solid #EDEDED;

      &:before,
      &:after {
        display: none;
      }
    }

    input {
      color: #000;
      font-size: 20px;
      font-weight: bold;
      letter-spacing: 0;
      line-height: 18px;
      height: 60px;

      &::placeholder {
        color: rgba(86, 86, 86, .5);
        font-size: 14px;
        font-weight: 500;
        letter-spacing: 0;
        line-height: 18px;
      }

      &::-moz-placeholder {
        line-height: 60px;
      }
    }

    .icon {
      width: 14px;
      height: 14px;
      background: url('~static/search.svg');
      background-size: contain;
      background-repeat: no-repeat;
      margin-right: 5px;
    }

    .clear {
      width: 16px;
      height: 16px;
      background: #AAAAAA;
      border-radius: 50%;
      cursor: pointer;
      border: none;
      position: relative;
      padding: 0;
      margin: 0;
      opacity: .5;
      flex-shrink: 0;

      &:before,
      &:after {
        content: '';
        position: absolute;
        bottom: 0;
        top: 0;
        right: 0;
        left: 0;
        margin: auto;
        height: 10px;
        width: 2px;
        background: #FFFFFF;
      }

      &:before {
        transform: rotate(45deg);
      }

      &:after {
        transform: rotate(-45deg);
      }
    }
  }

  .list-container {
    position: relative;
    background: #FFFFFF;
  }

  .list-overlay {
    background: rgba(0,0,0,.6);
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
    z-index: 10;
  }

  .seperator {
    margin: 0;
    height: 26px;
    width: 100%;
    background-color: #F7F8FC;
    color: #BBBCC1;
    font-size: 13px;
    line-height: 16px;
    padding-left: 30px;
    display: flex;
    align-items: center;
    position: sticky;
    top: 60px;
    z-index: 5;

    @include desktop-big {
       padding-left: 115px;
    }
  }

  .wrapper {
    position: absolute;
    bottom: -30px;
    left: -30px;
    right: -30px;
    min-height: 100vh;
    overflow: hidden;
    animation: set-z-index .1s both .4s;

    @include desktop {
      height: 500px;
      min-height: auto;
    }

    @include desktop-big {
      height: 600px;
      bottom: auto;
      left: -115px;
      right: auto;
      top: -190px;
      width: 610px;
    }

    &:after {
      content: '';
      transition: opacity .3s;
      height: 48px;
      width: 100%;
      position: absolute;
      z-index: 502;
      bottom: 0;
      pointer-events: none;
      box-shadow: none;
      background: linear-gradient(to top, rgba(0,0,0,.1), transparent);
      opacity: 0;
    }

    .white-underlay {
      display: none;
    }

    &.scrolled {
      animation: none;
      z-index: 501;

      &--top-shadow {
        &:before {
          z-index: inherit;
          content: '';
          height: 0;
          background: transparent;
          width: 100%;
          position: absolute;
          top: -15px;
          box-shadow: 0px 20px 20px 16px rgba(0, 0, 0, 0.08);
        }
      }

      .white-underlay {
        background: #FFFFFF;
        position: fixed;
        z-index: -1;
        display: block;
        bottom: 0;
        left: 0;
        right: 0;
        width: 100%;
        height: 300px;
        opacity: 0;
        animation: fade-in .1s 2s forwards;

        @include desktop {
          display: none;
        }
      }
    }

    &.can-scroll {
      &:after {
        opacity: 1;
      }
    }
  }


  .container {
    height: 100%;
    overflow: hidden;
    overflow-y: scroll;
    z-index: 8;
    position: relative;
    display: flex;
    flex-direction: column;
    transition: background .4s ease;
    background: transparent;

    &.dark {
      background: rgba(0,0,0,.6);
    }

    ul {
      user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      position: relative;
      background: #FFFFFF;
      font-size: 16px;

      &:first-of-type {
        > li:first-of-type {
          border-top: 1px solid #EDEDED;
        }
      }

      li {
        display: block;
        border-bottom: 1px solid #EDEDED;
        margin: 0 0 0 30px;
        padding: 20px 20px 20px 0;
        height: 60px;
        overflow: hidden;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        justify-content: space-between;
        list-style: none;
        color: #282B30;
        position: relative;
        cursor: pointer;

        &.prepend {
          justify-content: flex-start;

          @include desktop {
            font-size: 18px;
          }
        }

        &.hide-borders {
          border-bottom: 0;
        }

        &.selected {
          margin: 0;
          padding-left: 30px;
          border-color: $actionNormalColor;
          background: $actionNormalColor;
          color: #FFFFFF;

          img,
          .subtitle {
            display: none ;
          }

          @include desktop-big {
            margin: 0 auto;
            padding: 20px;
          }
        }

        img {
          height: 40px;
          width: auto;
          object-fit: cover;
        }

        .subtitle {
          color: #A6A6A6;
          font-size: 18px;
          line-height: 22px;
          &--strong {
            color: #3D3D3D;
            font-weight: bold;
          }
        }

        @include desktop-big {
          width: 420px;
          margin: 0 auto;
          padding: 20px;
        }
      }

      &.grouped {
        li {
          &:last-child {
            border-bottom: none;
          }
          &.prepend {
            height: 50px;
            &:last-child {
              margin-bottom: 10px;
            }
          }
        }
      }

      &.saving {
        li.selected {
          transition-duration: .2s;
          transition-property: background, color, border;
          background: $inputSaveColor;
          border-color: $inputSaveColor;
          &:after {
            content: ' ';
            display: inline-block;
            width: 10px;
            height: 10px;
            border-bottom: $nextButtonArrowThickness solid white;
            border-bottom: $nextButtonArrowThickness-portal solid white;
            border-right: $nextButtonArrowThickness solid white;
            border-right: $nextButtonArrowThickness-portal solid white;
            border-radius: 2px;
            transform: rotate(-45deg);
            margin: 0 0 -2px -5px;
            @include transition;
            transition-property: transform, background;
            position: absolute;
            right: 22px;
            top: 0;
            bottom: -2px;
            margin: auto;
            animation: arrow-check .4s cubic-bezier(0,-0.05,0,1.01) forwards;
          }
        }
      }
    }

    // to hide scrollbars
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      width: 0px;
      background: transparent;
    }
    //
  }


  @keyframes arrow-check {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
      width: 8px;
      height: 18px;
      transform: rotate(45deg);
      margin-bottom: 22px;
    }
  }

  @keyframes set-z-index {
    from {
      z-index: 501;
    }
    99% {
      z-index: 9;
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/SearchSelector.vue